import { LoadingState, QueryError, WaitStatus } from '#/components/common/loading-state'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Stepper } from '#/components/plan/stepper'
import { TechForm } from '#/components/plan/tech-form'
import { QuestionsForm } from '#/components/plan/questions-form'
import { GenerationProgress, useGenerationStream } from '#/components/plan/generation-progress'
import { PlanModelChip } from '#/components/common/model-picker'
import { PrdReview } from '#/components/plan/prd-review'
import { PlanReferences } from '#/components/plan/plan-references'
import { clearIdeaDraft } from '#/components/plan/idea-form'
import { SiteStep } from '#/components/site/site-step'
import { SiteReferenceCard, SiteStackHint } from '#/components/site/site-reference'
import { api, ApiError, qk, usePlan, useUpdatePlan, type JobPayload } from '#/lib/api'
import { useBuilderMode } from '#/lib/simple-mode'
import { pickGenerationJob } from '#/lib/generation-job'
import type { QuestionAnswers } from '#/lib/types'
import type { Answers } from '#/lib/grilling'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/plans/$planId')({
  validateSearch: z.object({ step: z.number().int().min(1).max(5).optional() }),
  component: Wizard,
})

function Wizard() {
  const { planId } = Route.useParams()
  const { step: qs } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading, error, isFetching, refetch } = usePlan(planId)
  const update = useUpdatePlan(planId)
  const plan = data?.plan
  // website reverse engineering: step 1 is the dissection (shown while it still runs, whatever the URL says)
  const site = data?.site ?? null
  const siteRunning = site?.status === 'queued' || site?.status === 'running'
  // non-tech F0: a simple plan has no tech step — step 2 collapses into step 3
  const simple = useBuilderMode().simple || plan?.summaryMode === 'simple'
  const rawStep = qs ?? plan?.step ?? 2
  const siteStep = Boolean(site) && (rawStep <= 1 || (siteRunning && rawStep <= 3))
  const step = siteStep ? 1 : simple && rawStep <= 2 ? 3 : rawStep
  const go = (s: number) => navigate({ to: '/plans/$planId', params: { planId }, search: { step: s } })
  // `workspaceId` may come from the job-status payload before the plan query has loaded
  const toWorkspace = (workspaceId: string | undefined = plan?.workspaceId) => {
    if (!workspaceId) return false
    clearIdeaDraft()
    navigate({ to: '/workspace/$workspaceId/$view', params: { workspaceId, view: 'roadmap' }, search: { plan: planId } })
    return true
  }

  // questions (step 3) — grilling rounds (docs/grilling/PLAN.md): the server returns the saved
  // interview at once, or writes round 1; `next` saves the answers and asks the following round.
  type QuestionsPayload = { state: QuestionAnswers; maxRounds: number }
  const [qa, setQa] = useState<QuestionsPayload | null>(null)
  const [qBusy, setQBusy] = useState(false)
  const [qNextBusy, setQNextBusy] = useState(false)
  const [qError, setQError] = useState(false)
  useEffect(() => {
    if (step !== 3 || !plan || qa || qBusy || qError) return
    setQBusy(true)
    api<QuestionsPayload>('/api/plan/questions', { method: 'POST', json: { planId, action: 'start' } })
      .then((r) => setQa({ state: r.state, maxRounds: r.maxRounds }))
      .catch((e) => { setQError(true); toast.error(e instanceof ApiError ? e.message : m.common_error_generic()) })
      .finally(() => setQBusy(false))
  }, [step, plan, qa, qBusy, qError, planId])
  const askNextRound = async (answers: Answers, skipped: string[]) => {
    setQNextBusy(true)
    try {
      const r = await api<QuestionsPayload>('/api/plan/questions', { method: 'POST', json: { planId, action: 'next', answers, skipped } })
      setQa({ state: r.state, maxRounds: r.maxRounds })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'questions_complete') {
        // the answers were saved; reload the saved interview (now complete)
        const r = await api<QuestionsPayload>('/api/plan/questions', { method: 'POST', json: { planId, action: 'start' } }).catch(() => null)
        if (r) setQa({ state: r.state, maxRounds: r.maxRounds })
      } else toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    } finally {
      setQNextBusy(false)
    }
  }
  const regenerateQuestions = async () => {
    setQBusy(true)
    setQa(null)
    try {
      const r = await api<QuestionsPayload>('/api/plan/questions', { method: 'POST', json: { planId, action: 'start', regenerate: true } })
      setQa({ state: r.state, maxRounds: r.maxRounds })
    } catch (e) {
      setQError(true)
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    } finally {
      setQBusy(false)
    }
  }

  // generation: step 4 = PRD (write, then review), step 5 = features & tasks
  const hasDoc = (data?.documents?.length ?? 0) > 0
  const stage: 'prd' | 'features' = step >= 5 ? 'features' : 'prd'
  const streaming = (step === 4 || step === 5) && plan?.status !== 'completed'
  const [genNonce, setGenNonce] = useState(0)
  const { job, streamLost } = useGenerationStream(planId, Boolean(streaming), genNonce)
  const [localJob, setLocalJob] = useState<JobPayload | null>(null)
  const [retrying, setRetrying] = useState(false)
  // a fresh start/retry response wins over a stale stream payload until the stream catches up
  const effJob = pickGenerationJob<JobPayload>(job, localJob)
  const jobWorkspaceId = effJob && 'workspaceId' in effJob ? (effJob as { workspaceId?: string }).workspaceId : undefined
  // PRD stage finished → refetch the plan so the review view renders the document
  useEffect(() => {
    if (step === 4 && effJob?.status === 'done') qc.invalidateQueries({ queryKey: qk.plan(planId) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effJob?.status, effJob?.jobId, step, planId])
  // features stage finished → workspace
  useEffect(() => {
    if (step === 5 && effJob?.status === 'done' && effJob.phase !== 'review') {
      qc.invalidateQueries({ queryKey: qk.plan(planId) })
      toWorkspace(plan?.workspaceId ?? jobWorkspaceId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effJob?.status, effJob?.phase, step, plan?.workspaceId, jobWorkspaceId])
  // completed plan → never show the wizard's step 4/5, go straight to the roadmap
  useEffect(() => {
    if (plan?.status === 'completed' && (step === 4 || step === 5)) toWorkspace()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.status, step])
  const startGen = async (opts: { stage: 'prd' | 'features'; regenerate?: boolean }) => {
    setRetrying(true)
    try {
      const r = await api<{ jobId?: string; job: JobPayload }>('/api/plan/generation/start', { method: 'POST', json: { planId, regenerate: opts.regenerate ?? false, stage: opts.stage } })
      setLocalJob({ ...r.job, jobId: r.job.jobId ?? r.jobId ?? null })
      setGenNonce((n) => n + 1)
      return true
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
      if (e instanceof ApiError && e.status === 402) navigate({ to: '/pricing', search: { voucher: undefined } })
      return false
    } finally {
      setRetrying(false)
    }
  }

  if (error) return <><AppHeader /><QueryError onRetry={refetch} retrying={isFetching} /></>
  if (isLoading || !plan) return <><AppHeader /><LoadingState layout="document" label={m.loading_document()} /></>

  return (
    <>
      <AppHeader showStats={false} center={<Stepper steps={[site ? m.site_step_dissect() : m.wizard_step_idea(), m.wizard_step_docs(), m.wizard_step_export()]} current={site && step <= 1 ? 0 : step >= 4 ? 2 : 1} />} />
      <main className={step === 4 && hasDoc && effJob?.status !== 'running' ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'flex-1 overflow-y-auto px-6 pb-12 pt-8 sm:px-8 sm:pt-12'}>
        {step === 1 && site ? (
          <SiteStep planId={planId} onStep={(s) => go(s)} />
        ) : step <= 2 ? (
          <>
          {site ? <SiteStackHint planId={planId} className="mx-auto mb-4 w-full max-w-2xl" /> : null}
          <TechForm
            initial={plan.techStack}
            busy={update.isPending || retrying}
            onBack={() => (site ? go(1) : navigate({ to: '/plan', search: { flow: 'new' } }))}
            onNext={async (techStack) => {
              try {
                await update.mutateAsync({ techStack, step: 3 })
                go(3)
              } catch { toast.error(m.common_error_generic()) }
            }}
          />
          </>
        ) : step === 3 ? (
          qa ? (
            <>
            {simple ? null : (
              <div className="mx-auto mb-3 flex w-full max-w-3xl items-center justify-end gap-2 text-xs text-muted-foreground">
                <span>{m.model_pick_label()}</span>
                <PlanModelChip planId={planId} value={plan.model ?? null} />
              </div>
            )}
            {site ? <SiteReferenceCard planId={planId} className="mx-auto mb-4 w-full max-w-2xl" /> : null}
            <PlanReferences planId={planId} disabled={update.isPending || retrying} className="mx-auto mb-4 w-full max-w-2xl" />
            <QuestionsForm
              key={`${qa.state.rounds ?? 1}-${qa.state.questions.length}`}
              state={qa.state}
              maxRounds={qa.maxRounds}
              plan={{ title: plan.title, idea: plan.idea, language: plan.language }}
              busy={update.isPending || retrying}
              nextBusy={qNextBusy}
              onBack={() => (simple ? (site ? go(1) : navigate({ to: '/plan', search: { flow: 'new' } })) : go(2))}
              onNext={askNextRound}
              onRegenerate={regenerateQuestions}
              onFinish={async (answers, skipped) => {
                try {
                  await update.mutateAsync({ questionAnswers: { ...qa.state, answers, skipped, done: true }, step: 4 })
                  if (await startGen({ stage: 'prd' })) go(4)
                  else await update.mutateAsync({ step: 3 })
                } catch { toast.error(m.common_error_generic()) }
              }}
            />
            </>
          ) : (
            <div className="mx-auto max-w-2xl">{qError ? <QueryError onRetry={() => setQError(false)} /> : <WaitStatus label={m.questions_loading()} description={m.loading_questions_hint()} tips />}</div>
          )
        ) : step === 4 && hasDoc && effJob?.status !== 'running' ? (
          <PrdReview
            planId={planId}
            documents={data!.documents}
            chatMessages={data?.chatMessages ?? []}
            busy={retrying || update.isPending}
            planModel={plan.model ?? null}
            job={effJob}
            onRegenerate={async () => {
              setLocalJob(null)
              if (await startGen({ stage: 'prd', regenerate: true })) qc.invalidateQueries({ queryKey: qk.plan(planId) })
            }}
            onContinue={async () => {
              if (await startGen({ stage: 'features' })) {
                try { await update.mutateAsync({ step: 5 }) } catch { toast.error(m.common_error_generic()) }
                go(5)
              }
            }}
          />
        ) : (
          <GenerationProgress job={effJob} stage={stage} streamLost={streamLost} retrying={retrying} onRetry={() => startGen({ stage })} />
        )}
      </main>
    </>
  )
}
